/**
 * Build a sensible <title> and <meta description> for treatments that
 * don't have explicit `seo` fields (minimal catalog entries).
 */
export const buildTreatmentSEO = (treatment) => {
  if (treatment?.seo?.title && treatment?.seo?.description) {
    return treatment.seo;
  }
  const name = treatment?.name || 'Treatment';
  const price = treatment?.startingPrice ? ` (${treatment.startingPrice})` : '';
  return {
    title: `${name} in Vietnam${price} — Request an Appointment | VietClinics`,
    description: `${treatment?.shortDesc || ''} Get matched with verified Vietnamese specialists through the VietClinics concierge — no clinic browsing, no pressure.`.trim(),
  };
};

/**
 * JSON-LD MedicalProcedure schema for flagship treatment pages.
 * Designed to power Google rich snippets for medical procedures.
 */
export const buildTreatmentJsonLd = (treatment, origin) => {
  if (!treatment) return null;
  const url = `${origin || ''}/treatments/${treatment.slug}`;
  const base = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: treatment.name,
    description: treatment.shortDesc,
    url,
    image: treatment.image,
    procedureType: 'https://schema.org/SurgicalProcedure',
    bodyLocation: treatment.category,
  };

  if (treatment.benefits) {
    base.benefits = treatment.benefits.map((b) => `${b.title}: ${b.desc}`).join(' | ');
  }
  if (treatment.recovery) {
    base.followup = treatment.recovery;
  }
  if (treatment.priceGuide && treatment.priceGuide.length > 0) {
    base.cost = {
      '@type': 'PriceSpecification',
      priceCurrency: 'USD',
      minPrice: treatment.priceGuide[0].price,
      description: treatment.priceGuide.map((p) => `${p.option}: ${p.price}`).join(' | '),
    };
  }
  return base;
};

/**
 * JSON-LD FAQPage schema — built from a treatment's FAQ array.
 */
export const buildFaqJsonLd = (faq) => {
  if (!Array.isArray(faq) || faq.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };
};

/**
 * Site-wide MedicalOrganization JSON-LD, used in app root.
 *
 * Uses `Organization + MedicalOrganization` dual-type so VietClinics can be
 * recognised both as a healthcare entity and a brand. We do NOT claim to be
 * a hospital/clinic ourselves — `description` clarifies the coordination role.
 */
export const buildOrganizationJsonLd = (origin) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'VietClinics',
  legalName: 'Viet Medica Ltd',
  url: origin || 'https://vietclinics.com',
  logo: `${origin || 'https://vietclinics.com'}/logo.png`,
  description: "VietClinics is an independent healthcare coordination and patient-support service in Vietnam for international patients, expats and travellers. We help people explore care options and coordinate appointments and patient support with selected licensed providers. VietClinics is not a clinic, hospital, ambulance service or medical provider and does not diagnose or treat patients.",
  areaServed: { '@type': 'Country', name: 'Vietnam' },
  knowsAbout: [
    'Dental treatment in Vietnam', 'Cosmetic surgery in Vietnam', 'Hair transplantation in Vietnam',
    'Eye care in Vietnam', 'Health check-ups in Vietnam', 'Urgent non-emergency care coordination',
    'English-speaking doctor coordination', 'Medical travel support',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    availableLanguage: ['English', 'Vietnamese'],
    areaServed: 'VN',
  },
  sameAs: [
    'https://www.facebook.com/vietclinics',
    'https://www.instagram.com/vietclinics',
    'https://www.linkedin.com/company/vietclinics',
  ],
});

/**
 * WebSite JSON-LD — enables Google sitelinks search box on brand SERPs.
 */
export const buildWebSiteJsonLd = (origin) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'VietClinics',
  url: origin || 'https://vietclinics.com',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${origin || 'https://vietclinics.com'}/treatments?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

/**
 * BreadcrumbList JSON-LD — accepts an ordered array of { name, url } items.
 */
export const buildBreadcrumbJsonLd = (items, origin) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url?.startsWith('http') ? item.url : `${origin || ''}${item.url}`,
    })),
  };
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* hreflang alternates                                                         */
/* ─────────────────────────────────────────────────────────────────────────── */

/** Supported locales (must match LanguageContext) — central source of truth. */
export const SUPPORTED_LOCALES_HREFLANG = [
  { code: 'en', hreflang: 'en' },
  { code: 'vi', hreflang: 'vi' },
  { code: 'fr', hreflang: 'fr' },
  { code: 'de', hreflang: 'de' },
  { code: 'ar', hreflang: 'ar' },
  { code: 'zh', hreflang: 'zh' },
  { code: 'ko', hreflang: 'ko' },
  { code: 'ja', hreflang: 'ja' },
  { code: 'ru', hreflang: 'ru' },
];

/**
 * Build hreflang `<link>` data for a given canonical path.
 * - Pass the locale-stripped path (e.g. "/treatments" — NOT "/vi/treatments").
 * - Returns an array of { hreflang, href } entries plus an `x-default` entry
 *   pointing to the English version.
 *
 * Render with Helmet:
 *   {alts.map(a => <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />)}
 */
export const buildHreflangAlternates = (path, origin = 'https://vietclinics.com') => {
  const cleanPath = (path || '/').replace(/\/+$/, '') || '/';
  const out = SUPPORTED_LOCALES_HREFLANG.map(({ code, hreflang }) => {
    const localizedPath =
      code === 'en'
        ? cleanPath
        : `/${code}${cleanPath === '/' ? '' : cleanPath}`;
    return { hreflang, href: `${origin}${localizedPath}` };
  });
  // x-default points to English (the canonical default for search engines
  // that haven't yet picked a locale)
  out.push({ hreflang: 'x-default', href: `${origin}${cleanPath}` });
  return out;
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* Open Graph defaults                                                         */
/* ─────────────────────────────────────────────────────────────────────────── */

/** Default OG image used as fallback when a page hasn't provided its own. */
export const DEFAULT_OG_IMAGE = 'https://customer-assets.emergentagent.com/job_5a81bd9f-a300-47ec-b521-267995465c4c/artifacts/c0pmwk0c_z7839008540484_82377df49358e26afcea9fd3bfe091fe.jpg';
